import React, { useRef, useState, useEffect, useMemo, useId } from "react";
import imagePlus from "../assets/image-plus.svg";
import Toast from "../components/Toast/toast.js";
import {convertToWebP, resizeImage} from "../utils/imageConvertUtils.js";
import { useLanguage } from "../utils/languageContext.js";
import { getTranslation } from "../utils/translate.js";
import Modal from "../components/comman/modal.js";
import Button from "../components/comman/button.js";
import LoadingSpinner from "../components/LoadingSpinner/loadingSpinner.js";

const PictureUploader = ({ dishImage, onImageRemoved, setImage, image, validationError, setValidationError = () => {}, isEditable= true, eagerLoad = false }) => {
    const reactId = useId().replace(/:/g, "");
    const fileInputWithPreviewId = `dish-file-with-preview-${reactId}`;
    const fileInputEmptyId = `dish-file-empty-${reactId}`;
    const { language } = useLanguage();
    const toastRef = useRef(null);
    const fileInputRef = useRef(null);
    const [previewImage, setPreviewImage] = useState(dishImage || null);
    const [error, setError] = useState(null);
    const [showImageModal, setShowImageModal] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const imageExplicitlyRemovedRef = useRef(false); // Track if user explicitly removed the image
    
    // Normalize image URL to prevent unnecessary re-renders (remove query params for comparison)
    const normalizedDishImage = useMemo(() => {
        if (!dishImage) return null;
        // Remove query parameters and hash for comparison
        return dishImage.split('?')[0].split('#')[0];
    }, [dishImage]);

    useEffect(() => {
        if (image && setValidationError) {
            setValidationError((prev) => ({
                ...prev,
                url: "",
            }));
        }
    }, [image, setValidationError]);

    // Update preview when dishImage prop changes (for edit mode)
    // Only restore from dishImage if image wasn't explicitly removed
    useEffect(() => {
        // Don't restore if user explicitly removed the image
        if (imageExplicitlyRemovedRef.current && !image) {
            return;
        }
        
        // If we have a local image file, use that (don't override with dishImage)
        if (image) {
            return;
        }
        
        // If we have a dishImage and no local image file
        if (normalizedDishImage) {
            // Only update if the normalized URL actually changed (avoid unnecessary re-renders)
            const currentNormalized = previewImage ? previewImage.split('?')[0].split('#')[0] : '';
            
            if (currentNormalized !== normalizedDishImage) {
                setPreviewImage(dishImage); // Use original URL with query params if any
            }
        } else if (!normalizedDishImage) {
            // If dishImage is null and we don't have a local image, clear preview
            if (previewImage && !previewImage.startsWith('blob:')) {
                setPreviewImage(null);
            }
        }
    }, [normalizedDishImage, dishImage, image, previewImage]); // Use normalized URL for comparison

    // Reset the flag when a new image is uploaded
    useEffect(() => {
        if (image) {
            imageExplicitlyRemovedRef.current = false;
        }
    }, [image]);

    const handleImageClick = (e) => {
        if (previewImage) {
            e.preventDefault();
            e.stopPropagation();
            setShowImageModal(true);
        }
    };

    const handleChangeImage = () => {
        if (!isEditable) {
            return;
        }
        if (fileInputRef.current) {
            try {
                fileInputRef.current.click();
            } catch {
                // Programmatic click can fail in rare browser/security contexts
            }
        }
    };

    const processImageFile = async (file) => {
        if (!file) {
            return;
        }

        // Clear previous errors
        setError(null);

        // Validate file type before processing (MIME can be empty when dropping from some apps)
        const validImageTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif"];
        const ext = file.name.split(".").pop()?.toLowerCase() || "";
        const validExtensions = ["jpg", "jpeg", "png", "webp", "heic", "heif"];
        const mimeOk = validImageTypes.includes(file.type);
        const extOk = validExtensions.includes(ext);
        const typeOk =
            mimeOk ||
            (!file.type && extOk) ||
            (file.type === "application/octet-stream" && extOk);
        if (!typeOk) {
            const fileExtension = file.name.split('.').pop()?.toUpperCase() || 'unknown';
            // Try to get translation with extension placeholder, fallback to simple message
            let errorMessage = getTranslation('menu.invalidFileTypeWithExtension', language);
            if (errorMessage && errorMessage.includes('{extension}')) {
                errorMessage = errorMessage.replace('{extension}', fileExtension);
            } else {
                // Fallback to simple message with extension
                errorMessage = getTranslation('menu.invalidFileType', language) || 
                    `Invalid file type (${fileExtension}). Please provide an image file (JPG, PNG, JPEG, WEBP, HEIC, HEIF).`;
            }
            setError(errorMessage);
            setImage(null);
            setPreviewImage(null);
            // Also show toast notification for better visibility
            if (toastRef.current) {
                toastRef.current.addToast(errorMessage, "error", 5000);
            }
            return;
        }

        // Start loading
        setIsUploading(true);
        setError(null);

        try {
            const reader = new FileReader();
            reader.onload = async (e) => {
                const img = new Image();
                img.src = e.target.result;

                img.onload = async () => {
                    try {
                    let processedFile = file;

                        // Resize only if image is larger than maximum allowed dimensions
                    if (img.width > 1400 || img.height > 800) {
                        processedFile = await resizeImage(img, 1400, 800);
                    }

                    const convertedFile = await convertToWebP(processedFile);

                    const finalReader = new FileReader();
                    finalReader.onload = (e) => {
                        const webpImg = new Image();
                        webpImg.onload = () => {
                                // Accept the image regardless of size (small images are fine)
                                // Only reject if there was an error during conversion
                                setError(null);
                                setImage(convertedFile);
                                setPreviewImage(URL.createObjectURL(convertedFile));
                                setIsUploading(false);
                        };
                        webpImg.onerror = () => {
                            const errorMessage = getTranslation('menu.imageConversionError', language) || 
                                "Error converting image to WebP format. The file may be corrupted. Please try a different image.";
                            setError(errorMessage);
                            setImage(null);
                            setPreviewImage(null);
                            setIsUploading(false);
                            // Show toast notification for better visibility
                            if (toastRef.current) {
                                toastRef.current.addToast(errorMessage, "error", 5000);
                            }
                        };
                        webpImg.src = e.target.result;
                    };

                    finalReader.readAsDataURL(convertedFile);
                    } catch (conversionError) {
                        // Handle conversion errors (including invalid file type from convertToWebP)
                        let errorMessage = conversionError.message || 
                            getTranslation('menu.imageConversionError', language) || 
                            "Error processing image. Please try again.";
                        
                        // Provide more specific error message for invalid file type
                        if (conversionError.message && conversionError.message.includes("Invalid file type")) {
                            const fileExtension = file.name.split('.').pop()?.toUpperCase() || 'unknown';
                            errorMessage = getTranslation('menu.invalidFileType', language) || 
                                `Invalid file type (${fileExtension}). Please provide an image file (JPG, PNG, JPEG, WEBP, HEIC, HEIF).`;
                        }
                        
                        setError(errorMessage);
                        setImage(null);
                        setPreviewImage(null);
                        setIsUploading(false);
                        // Show toast notification for better visibility
                        if (toastRef.current) {
                            toastRef.current.addToast(errorMessage, "error", 5000);
                        }
                    }
                };

                img.onerror = () => {
                    const errorMessage = getTranslation('menu.imageLoadError', language) || 
                        "Error loading image. The file may be corrupted or in an unsupported format. Please try a different image.";
                    setError(errorMessage);
                    setImage(null);
                    setPreviewImage(null);
                    setIsUploading(false);
                    // Show toast notification for better visibility
                    if (toastRef.current) {
                        toastRef.current.addToast(errorMessage, "error", 5000);
                    }
                };
            };

            reader.onerror = () => {
                const errorMessage = getTranslation('menu.imageLoadError', language) || 
                    "Error reading the file. The file may be corrupted or inaccessible. Please try again.";
                setError(errorMessage);
                setImage(null);
                setPreviewImage(null);
                setIsUploading(false);
                // Show toast notification for better visibility
                if (toastRef.current) {
                    toastRef.current.addToast(errorMessage, "error", 5000);
                }
            };

            reader.readAsDataURL(file);
        } catch (error) {
            // Catch any unexpected errors
            let errorMessage = error.message || 
                getTranslation('menu.imageConversionError', language) || 
                "An error occurred while processing the image. Please try again.";
            
            // Provide more specific error message for invalid file type
            if (error.message && error.message.includes("Invalid file type")) {
                const fileExtension = file?.name?.split('.').pop()?.toUpperCase() || 'unknown';
                errorMessage = getTranslation('menu.invalidFileType', language) || 
                    `Invalid file type (${fileExtension}). Please provide an image file (JPG, PNG, JPEG, WEBP, HEIC, HEIF).`;
            }
            
            setError(errorMessage);
            setImage(null);
            setPreviewImage(null);
            setIsUploading(false);
            // Show toast notification for better visibility
            if (toastRef.current) {
                toastRef.current.addToast(errorMessage, "error", 5000);
            }
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }
        // Reset file input to allow selecting the same file again
        event.target.value = "";
        processImageFile(file);
    };

    const handleImageDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer) {
            e.dataTransfer.dropEffect = "copy";
        }
    };

    const handleImageDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const file = e.dataTransfer.files?.[0];
        if (file) {
            processImageFile(file);
        }
    };

    const handleImageRemove = () => {
        setPreviewImage(null);
        setImage(null);
        setError(null);
        imageExplicitlyRemovedRef.current = true; // Mark that user explicitly removed the image
        onImageRemoved?.();
    };

    return (
        <div className="flex flex-col gap-4 rounded-md w-full">
            <Toast ref={toastRef} />
            {/* Image Viewing Modal */}
            <Modal
                isOpen={showImageModal}
                onClose={() => setShowImageModal(false)}
                size="4xl"
                className="p-0"
            >
                <div className="relative">
                    <img 
                        src={previewImage || dishImage || ""} 
                        alt="Dish" 
                        className="max-w-full max-h-[90vh] object-contain w-full"
                        onLoad={(e) => {
                            e.target.style.opacity = "1";
                        }}
                        onError={(e) => {
                            console.error("Error loading image in modal:", previewImage || dishImage);
                            e.target.style.display = "none";
                        }}
                        loading={eagerLoad ? "eager" : "lazy"}
                        fetchpriority="low"
                        decoding="async"
                    />
                </div>
            </Modal>

            <div className="relative">
                {isUploading && (
                    <div className="mb-4 p-4 bg-gray-50 dark:bg-[#0d1117] border border-gray-300 dark:border-[#30363d] rounded-lg flex items-center justify-center">
                        <LoadingSpinner size="default" />
                        <span className="ml-3 text-sm text-gray-600 dark:text-[#8b949e]">
                            {getTranslation('common.processing', language) || 'Processing image...'}
                        </span>
                    </div>
                )}
                {previewImage && !isUploading && (
                    <div
                        className="relative w-full mb-4"
                        onDragOver={isEditable ? handleImageDragOver : undefined}
                        onDrop={isEditable ? handleImageDrop : undefined}
                    >
                        <div 
                            className="overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-all duration-300 relative group cursor-pointer"
                            onClick={handleImageClick}
                        >
                            <img
                                src={previewImage || ""}
                                alt="Preview"
                                className="w-full h-auto object-cover rounded-lg"
                                style={{ maxHeight: "250px" }}
                                onLoad={(e) => {
                                    // Image loaded successfully
                                    e.target.style.opacity = "1";
                                }}
                                onError={(e) => {
                                    // Handle image load error
                                    console.error("Error loading image:", previewImage);
                                    e.target.style.display = "none";
                                }}
                                loading={eagerLoad ? "eager" : "lazy"}
                                fetchpriority="low"
                                decoding="async"
                            />
                            {/* Hover overlay with "Click to view" text */}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center transition-all duration-200 opacity-0 group-hover:opacity-100 pointer-events-none">
                                <div className="text-white text-sm font-medium bg-black bg-opacity-70 px-4 py-2 rounded">
                                    {getTranslation('profile.clickToView', language) || getTranslation('menu.clickToView', language) || 'Click to view'}
                                </div>
                            </div>
                        </div>
                        {isEditable && (
                            <div className="flex gap-2 mt-3">
                                <Button
                                    variant="secondary"
                                    size="md"
                                    type="button"
                                    onClick={handleChangeImage}
                                    className="flex items-center justify-center"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    {getTranslation('profile.changeImage', language) || getTranslation('menu.changeImage', language) || 'Change Image'}
                                </Button>
                                <Button
                                    variant="danger"
                                    size="md"
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleImageRemove();
                                    }}
                                    className="flex items-center justify-center"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    {getTranslation('common.delete', language) || 'Delete'}
                                </Button>
                            </div>
                        )}
                        <input
                            ref={fileInputRef}
                            id={fileInputWithPreviewId}
                            type="file"
                            className="hidden"
                            onChange={handleFileChange}
                            accept="image/*"
                            disabled={!isEditable}
                        />
                    </div>
                )}

                {error && (
                    <div className="mb-2 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-md">
                        <div className="flex items-start gap-2">
                            <svg className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-sm font-medium text-red-700 dark:text-red-400">
                                {error}
                            </p>
                        </div>
                    </div>
                )}

                {!previewImage && !isUploading && isEditable && (
                    <div
                        className={`flex items-center justify-center w-full h-40 border-2 mt-1 ${
                            validationError || error ? "border-red-500 dark:border-red-400" : "border-gray-300 dark:border-[#30363d]"
                        } border-dashed rounded-lg bg-gray-50 dark:bg-[#0d1117] cursor-pointer hover:bg-gray-100 dark:hover:bg-[#161b22] transition-colors duration-200`}
                        onDragOver={handleImageDragOver}
                        onDrop={handleImageDrop}
                    >
                        <label
                            htmlFor={fileInputEmptyId}
                            className="relative flex items-center justify-center w-full h-full cursor-pointer"
                        >
                            <div className="text-center p-4">
                                <div className="flex justify-center mb-2">
                                    <img 
                                        src={imagePlus || ""} 
                                        alt="Add" 
                                        className="w-8 h-8 mx-auto transition-transform duration-200 hover:scale-110 dark:invert dark:opacity-80"
                                    />
                                </div>
                                <p className="text-sm font-medium text-gray-500 dark:text-[#8b949e] mb-1">
                                    {getTranslation('menu.clickToUploadImage', language) || 'Click to upload an image'}
                                </p>
                                <p className="text-xs text-gray-400 dark:text-[#6e7681]">
                                    {getTranslation('menu.fileType', language) || 'File type: JPG, PNG, JPEG, WEBP'}
                                </p>
                            </div>
                            <input
                                id={fileInputEmptyId}
                                type="file"
                                className="hidden"
                                onChange={handleFileChange}
                                accept="image/*"
                            />
                        </label>
                    </div>
                )}
                {!previewImage && !isUploading && !isEditable && (
                    <div className="flex items-center justify-center w-full h-40 border-2 mt-1 border-gray-300 dark:border-[#30363d] border-dashed rounded-lg bg-gray-50 dark:bg-[#0d1117]">
                        <p className="text-sm text-gray-400 dark:text-[#6e7681]">
                            {getTranslation('profile.noImage', language) || 'No image'}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PictureUploader;
